import React from "react";
import { useI18n } from "@/lib/i18n";
import { labelClass } from "@/components/Brand";
import { MandatoryPrintRow, LegacyQuotaRows } from "@/components/MandatoryPrintParts";
import { rowKey } from "@/lib/rowKey";

/**
 * Mandatory (pre-paid) print quotas picked straight from the studio's master price
 * catalogue — click a size to include it, then set how many copies the package covers.
 */
export const MandatoryPrintPicker = ({ catalog, items, onChange, testIdPrefix = "mandatory-print" }) => {
  const { t } = useI18n();
  const prices = catalog || {};
  const sizes = Object.keys(prices);
  const rows = items || [];
  const rowOf = (size) => rows.find((i) => i.size === size);
  const orphans = rows.filter((i) => i.size && !(i.size in prices));

  const toggle = (size) =>
    onChange(rowOf(size)
      ? rows.filter((i) => i.size !== size)
      : [...rows, { size, qty: 1, frame_required: false, _k: rowKey() }]);

  const setQty = (size, qty) => onChange(rows.map((i) => (i.size === size ? { ...i, qty } : i)));
  const removeSize = (size) => onChange(rows.filter((i) => i.size !== size));

  if (sizes.length === 0) {
    return (
      <div>
        <label className={labelClass}>{t("print_quotas")}</label>
        <p className="mt-2 text-xs text-[#707070]" data-testid={`${testIdPrefix}-empty`}>{t("no_catalog_sizes")}</p>
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <label className={labelClass}>{t("print_quotas")}</label>
        <span className="font-mono-accent text-[10px] tracking-[0.14em] uppercase text-[#707070]"
          data-testid={`${testIdPrefix}-count`}>
          {t("n_selected", { n: rows.length })}
        </span>
      </div>
      <p className="mt-1 mb-3 text-[11px] text-[#707070]">{t("mandatory_prints_hint")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
        {sizes.map((size) => (
          <MandatoryPrintRow key={size} size={size} price={prices[size]} row={rowOf(size)}
            onToggle={() => toggle(size)} onQty={(qty) => setQty(size, qty)}
            testIdPrefix={testIdPrefix} />
        ))}
      </div>

      {rows.length > 0 && (
        <button type="button" data-testid={`${testIdPrefix}-clear`} onClick={() => onChange([])}
          className="mt-3 text-[10px] tracking-[0.18em] uppercase text-[#707070] hover:text-[#F3F3F1]">
          {t("clear")}
        </button>
      )}

      {orphans.length > 0 && (
        <LegacyQuotaRows rows={orphans} onQty={setQty} onRemove={removeSize} testIdPrefix={testIdPrefix} />
      )}
    </div>
  );
};
